import { ChatMessageWithParts, ContentType } from "../../types";
import type {
  AssistantResponseSources,
  ProvenanceExternalSource,
} from "../../types/provenance";
import { countProvenanceTiers } from "./provenanceCounts";

/**
 * Reserved band for synthetic external cite ids — mirror of the backend
 * `EXTERNAL_CITE_BASE` (`mre/utils/citations.py`). Real DataChunk ids never
 * reach it, so an id at/above this is unambiguously an external source.
 */
export const EXTERNAL_CITE_BASE = 1e15;

export interface ClientWebExternal {
  cite_id: number;
  title?: string;
  url?: string;
  snippet?: string;
  source_type: "web";
}

/** One live web-search query (Searched-tier breadth), parsed from the streaming
 * store the moment the user approves — so the Searched row shows live, the same
 * way {@link ClientWebExternal} cards do, without waiting on the backend's
 * read-time projection (#20423 web-searched-live). */
export interface ClientWebSearchQuery {
  query: string;
  resultCount: number;
}

/** Strip the `<tool_response>…</tool_response>` wrapper WebSearchApproval adds. */
function stripToolResponseTags(text: string): string {
  return text
    .replace(/^<tool_response>\s*/, "")
    .replace(/\s*<\/tool_response>$/, "");
}

/**
 * Separator for {@link webSearchToolTextsSnapshot}. NUL can't appear in JSON
 * tool_response text, so joining on it is lossless.
 */
const SNAPSHOT_SEPARATOR = "\u0000";

/**
 * The tool_response texts that might carry web-search results, folded into ONE
 * string. Built as a `useSyncExternalStore` snapshot: it's a primitive, so an
 * unchanged value (the common case — the streaming store updates on every
 * token, the search results almost never) compares equal and skips the render.
 */
export function webSearchToolTextsSnapshot(
  messages: ChatMessageWithParts[] | undefined,
): string {
  const texts: string[] = [];
  for (const msg of messages ?? []) {
    for (const part of msg.contentParts ?? []) {
      if (part.type !== ContentType.TOOL_RESPONSE || !part.text) continue;
      // Cheap pre-filter so per-token snapshots don't JSON.parse every part.
      if (!part.text.includes("search_results")) continue;
      texts.push(part.text);
    }
  }
  return texts.join(SNAPSHOT_SEPARATOR);
}

/** Parse a {@link webSearchToolTextsSnapshot} back into the externals map. */
export function collectWebExternalsFromToolTexts(
  snapshot: string,
): Map<number, ClientWebExternal> {
  const out = new Map<number, ClientWebExternal>();
  if (!snapshot) return out;
  for (const text of snapshot.split(SNAPSHOT_SEPARATOR)) {
    let data: unknown;
    try {
      data = JSON.parse(stripToolResponseTags(text));
    } catch {
      continue;
    }
    const results = (data as { search_results?: { results?: unknown } })
      ?.search_results?.results;
    if (!Array.isArray(results)) continue;
    for (const r of results) {
      const citeId = (r as { cite_id?: unknown })?.cite_id;
      if (typeof citeId !== "number" || citeId < EXTERNAL_CITE_BASE) continue;
      if (!out.has(citeId)) {
        const rec = r as ClientWebExternal;
        out.set(citeId, {
          cite_id: citeId,
          title: rec.title,
          url: rec.url,
          snippet: rec.snippet,
          source_type: "web",
        });
      }
    }
  }
  return out;
}

/**
 * Parse each web-search tool_response into its Searched-tier query row. The
 * query lives on `search_results.query` (falling back to the stitched
 * `final_query`); the result count mirrors the backend's `result_count`
 * (`_web_search_events_from_utterance`). Deduped by query text so the same
 * search stitched into several parts yields one row.
 */
export function collectWebSearchQueriesFromToolTexts(
  snapshot: string,
): ClientWebSearchQuery[] {
  const byQuery = new Map<string, ClientWebSearchQuery>();
  if (!snapshot) return [];
  for (const text of snapshot.split(SNAPSHOT_SEPARATOR)) {
    let data: unknown;
    try {
      data = JSON.parse(stripToolResponseTags(text));
    } catch {
      continue;
    }
    const d = data as {
      final_query?: unknown;
      search_results?: { query?: unknown; results?: unknown };
    };
    const results = d?.search_results?.results;
    if (!Array.isArray(results)) continue;
    const query =
      (typeof d.search_results?.query === "string" && d.search_results.query) ||
      (typeof d.final_query === "string" && d.final_query) ||
      "";
    if (!query) continue;
    if (!byQuery.has(query)) {
      byQuery.set(query, { query, resultCount: results.length });
    }
  }
  return [...byQuery.values()];
}

/**
 * Web-search results reach the browser the moment the user approves: the
 * approve endpoint returns them and WebSearchApproval stitches them onto the
 * message's tool_response part as `search_results`. Each result already carries
 * the synthetic `cite_id` the model cites, so inline [N] numbering can resolve
 * LIVE from data already in the conversation — no `/messages/provenance` fetch,
 * no poll, no reload. This is the client mirror of the backend's
 * `collect_web_external_cite_ids` (#20423 A2).
 */
export function collectWebExternalsFromMessages(
  messages: ChatMessageWithParts[] | undefined,
): Map<number, ClientWebExternal> {
  return collectWebExternalsFromToolTexts(webSearchToolTextsSnapshot(messages));
}

/**
 * All TEXT-part content of a dialogue, in message order — the haystack for
 * {@link citedExternalIdsInOrder} when scanning a whole live conversation.
 */
export function concatTextParts(
  messages: ChatMessageWithParts[] | undefined,
): string {
  const texts: string[] = [];
  for (const msg of messages ?? []) {
    for (const part of msg.contentParts ?? []) {
      if (part.type === ContentType.TEXT && part.text) texts.push(part.text);
    }
  }
  return texts.join("\n");
}

/**
 * Fold LIVE web-search externals into a provenance payload for the Sources
 * panel. The backend payload stays authoritative — live entries are only
 * appended for cite_ids it doesn't own yet, numbered after its last cited
 * slot in first-cited order (matching the inline markers' provisional
 * numbering, so panel and markers agree). Returns the payload identity when
 * there is nothing to add. Adding sources also clears the `no_documents`
 * degradation — that notice describes a payload we now know is just stale.
 */
export function overlayLiveWebExternals(
  provenance: AssistantResponseSources,
  liveExternals: Map<number, ClientWebExternal>,
  citedIdsInOrder: number[],
  liveQueries: ClientWebSearchQuery[] = [],
): AssistantResponseSources {
  if (liveExternals.size === 0) return provenance;
  const owned = new Set(
    (provenance.external_sources ?? [])
      .map((e) => e.cite_id)
      .filter((id): id is number => id != null),
  );
  const fresh = [...liveExternals.values()].filter(
    (e) => !owned.has(e.cite_id),
  );
  if (fresh.length === 0) return provenance;

  const citedSet = new Set(citedIdsInOrder);
  const counts = countProvenanceTiers(provenance);
  let nextIndex = Math.max(
    counts.citedCount,
    ...(provenance.external_sources ?? []).map((e) => e.citation_index ?? 0),
  );
  const freshById = new Map(fresh.map((e) => [e.cite_id, e]));
  const appended: ProvenanceExternalSource[] = [];
  // Cited entries first, in cite order, so their numbers are deterministic.
  for (const id of citedIdsInOrder) {
    const live = freshById.get(id);
    if (!live) continue;
    freshById.delete(id);
    nextIndex += 1;
    appended.push({
      source_type: "web",
      title: live.title ?? null,
      identifier: live.url ?? null,
      url: live.url ?? null,
      snippet: live.snippet ?? null,
      cited: true,
      cite_id: id,
      citation_index: nextIndex,
    });
  }
  for (const live of freshById.values()) {
    appended.push({
      source_type: "web",
      title: live.title ?? null,
      identifier: live.url ?? null,
      url: live.url ?? null,
      snippet: live.snippet ?? null,
      cited: citedSet.has(live.cite_id),
      cite_id: live.cite_id,
      citation_index: null,
    });
  }
  // Live Searched rows for the web searches the backend hasn't projected yet
  // (fresh externals ⇒ this turn isn't in the payload). Unlike the result
  // cards, the Searched query row is backend-only, so without this it stays
  // empty until a reload — worse when stream-finalize doesn't fire the poll.
  // Deduped by query text so a re-projected backend row never doubles.
  const existingQueries = new Set(
    (provenance.searched ?? []).map((s) => s.query_text),
  );
  const searchedAdditions = liveQueries
    .filter((q) => !existingQueries.has(q.query))
    .map((q) => ({
      query_text: q.query,
      dataset_ids: [],
      collection_names: [],
      document_count: 0,
      result_count: q.resultCount,
      agent: null,
      tool_name: "web_search",
    }));

  return {
    ...provenance,
    external_sources: [...(provenance.external_sources ?? []), ...appended],
    searched: searchedAdditions.length
      ? [...(provenance.searched ?? []), ...searchedAdditions]
      : provenance.searched,
    degradation: provenance.degradation
      ? { ...provenance.degradation, no_documents: null }
      : provenance.degradation,
  };
}

// The model emits `<cite>N</cite>`; process_citations may rewrite it mid-stream
// to the resolved `<cite>{…}</cite>` form. Cover both.
const CITE_NUMERIC = /<cite>(\d+)<\/cite>/g;
const CITE_JSON = /<cite>(\{.*?\})<\/cite>/g;

/**
 * External cite ids cited in `content`, in first-seen order. Restricted to ids
 * present in `known` so a stray number in prose can't mint a phantom citation.
 * First-seen order matches the backend's dialogue-global numbering for a
 * single-turn web answer, so numbers don't shift when provenance later loads.
 */
export function citedExternalIdsInOrder(
  content: string,
  known: Map<number, ClientWebExternal>,
): number[] {
  const found: Array<[number, number]> = []; // [position, cite_id]
  let m: RegExpExecArray | null;
  CITE_NUMERIC.lastIndex = 0;
  while ((m = CITE_NUMERIC.exec(content)) !== null) {
    const id = Number(m[1]);
    if (known.has(id)) found.push([m.index, id]);
  }
  CITE_JSON.lastIndex = 0;
  while ((m = CITE_JSON.exec(content)) !== null) {
    try {
      const payload = JSON.parse(m[1]) as {
        cite_id?: unknown;
        chunk_id?: unknown;
      };
      const raw = payload?.cite_id ?? payload?.chunk_id;
      if (typeof raw === "number" && known.has(raw)) found.push([m.index, raw]);
    } catch {
      // malformed payload — ignore
    }
  }
  found.sort((a, b) => a[0] - b[0]);
  const seen = new Set<number>();
  const out: number[] = [];
  for (const [, id] of found) {
    if (!seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  }
  return out;
}
